import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Lock, Trash2, ChevronLeft, ChevronRight,
  Plus, RefreshCw, CheckCircle2, AlertTriangle, X, Check,
} from "lucide-react";
import { fetchTables, fetchTableData, upsertRow, deleteRow } from "../../services/sadtService";
import { runAll as runAllCalculators } from "../../services/calculationService";
import { toast } from "../../stores/toastStore";
import { confirm } from "../../components/ConfirmDialog";

const PAGE_SIZE = 100;

export default function DataTablePage() {
  const { table } = useParams();
  const decodedTable = decodeURIComponent(table);

  const [calcTables,     setCalcTables]     = useState([]);
  const [tableData,      setTableData]      = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [page,           setPage]           = useState(0);
  const [editCell,       setEditCell]       = useState(null); // { rowId, col, value, origValue }
  const [savingRows,     setSavingRows]     = useState(new Set());
  const [newRow,         setNewRow]         = useState(null); // null | { col: "" }
  const [newRowSaving,   setNewRowSaving]   = useState(false);
  const [isDirty,        setIsDirty]        = useState(false);
  const [recalcState,    setRecalcState]    = useState("idle"); // idle | running | done | error
  const [recalcResult,   setRecalcResult]   = useState(null);

  const inputRef       = useRef(null);
  const newRowFirstRef = useRef(null);

  const isReadOnly = calcTables.includes(decodedTable);
  const columns    = (tableData?.columns ?? []).filter((c) => c !== "_rowid");
  const rows       = tableData?.rows ?? [];
  const total      = tableData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const showBanner = !isReadOnly && (isDirty || recalcState === "running" || recalcState === "done" || recalcState === "error");

  // ── Data loading ──────────────────────────────────────────────────────
  useEffect(() => {
    fetchTables()
      .then((d) => setCalcTables(d.calculated_tables || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setEditCell(null);
    fetchTableData(decodedTable, { limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then((d) => setTableData(d))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [decodedTable, page]);

  useEffect(() => {
    if (editCell) inputRef.current?.focus();
  }, [editCell?.rowId, editCell?.col]);

  useEffect(() => {
    if (newRow) newRowFirstRef.current?.focus();
  }, [newRow != null]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Shared recalculate (called automatically after every mutation) ────
  function triggerRecalculate() {
    // Skip if already running — the in-flight calc will read the latest DB state
    if (recalcState === "running") return;
    setRecalcState("running");
    setRecalcResult(null);
    setIsDirty(false);
    runAllCalculators()
      .then((result) => {
        setRecalcResult(result);
        setRecalcState("done");
      })
      .catch((err) => {
        setRecalcState("error");
        toast.error(`Recalculation failed: ${err.message}`);
      });
  }

  // ── Cell editing ──────────────────────────────────────────────────────
  function startEdit(rowId, col, value) {
    if (isReadOnly) return;
    setEditCell({ rowId, col, value: value ?? "", origValue: value ?? "" });
  }

  async function commitEdit() {
    if (!editCell) return;
    const { rowId, col, value, origValue } = editCell;
    setEditCell(null);
    if (String(value) === String(origValue)) return;

    setSavingRows((prev) => new Set([...prev, rowId]));
    setTableData((prev) => ({
      ...prev,
      rows: prev.rows.map((r) => r._rowid === rowId ? { ...r, [col]: value } : r),
    }));

    try {
      await upsertRow(decodedTable, { _rowid: rowId, [col]: value });
      triggerRecalculate(); // fire-and-forget — user can keep editing while it runs
    } catch (err) {
      setTableData((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => r._rowid === rowId ? { ...r, [col]: origValue } : r),
      }));
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSavingRows((prev) => { const s = new Set(prev); s.delete(rowId); return s; });
    }
  }

  function handleEditKeyDown(e) {
    if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); commitEdit(); }
    if (e.key === "Escape") { setEditCell(null); }
  }

  // ── Delete row ────────────────────────────────────────────────────────
  async function handleDelete(rowId) {
    const ok = await confirm({
      title: "Delete row",
      description: "This row will be permanently removed and cannot be recovered.",
      confirmLabel: "Delete row",
      tone: "danger",
    });
    if (!ok) return;

    const snapshot = tableData;
    setTableData((prev) => ({
      ...prev,
      rows: prev.rows.filter((r) => r._rowid !== rowId),
      total: prev.total - 1,
    }));

    try {
      await deleteRow(decodedTable, rowId);
      toast.success("Row deleted");
      triggerRecalculate();
    } catch (err) {
      setTableData(snapshot);
      toast.error(err.message);
    }
  }

  // ── Insert row ────────────────────────────────────────────────────────
  function startAddRow() {
    if (isReadOnly) return;
    setNewRow(Object.fromEntries(columns.map((c) => [c, ""])));
    setEditCell(null);
  }

  function cancelNewRow() { setNewRow(null); }

  async function handleInsert() {
    if (!newRow || newRowSaving) return;
    const payload = Object.fromEntries(Object.entries(newRow).filter(([, v]) => v !== ""));
    if (Object.keys(payload).length === 0) {
      toast.error("Fill in at least one field before saving");
      return;
    }

    setNewRowSaving(true);
    try {
      await upsertRow(decodedTable, payload);
      setNewRow(null);
      const d = await fetchTableData(decodedTable, { limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      setTableData(d);
      toast.success("Row inserted");
      triggerRecalculate();
    } catch (err) {
      toast.error(`Insert failed: ${err.message}`);
    } finally {
      setNewRowSaving(false);
    }
  }

  function handleNewRowKeyDown(e) {
    if (e.key === "Escape") { cancelNewRow(); }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleInsert(); }
  }

  // ── Manual recalculate (banner button) ───────────────────────────────
  async function handleRecalculate() {
    if (recalcState === "running") return;
    setRecalcState("running");
    setRecalcResult(null);
    try {
      const result = await runAllCalculators();
      setRecalcResult(result);
      setRecalcState("done");
      setIsDirty(false);
      // Refresh data in case the viewed table changed
      const d = await fetchTableData(decodedTable, { limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      setTableData(d);
    } catch (err) {
      setRecalcState("error");
      toast.error(`Recalculation failed: ${err.message}`);
    }
  }

  function dismissBanner() {
    setRecalcState("idle");
    setIsDirty(false);
    setRecalcResult(null);
  }

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col overflow-hidden">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="shrink-0 px-8 lg:px-12 pt-8 pb-4 border-b border-[var(--color-rule)] flex items-end justify-between gap-4">
        <div>
          <Link
            to="/app/data"
            className="reveal inline-flex items-center gap-1.5 mb-4 hover:text-[var(--color-ink)] transition-colors"
            style={{ fontSize: "9px", fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-ink-3)" }}
          >
            <ArrowLeft size={11} /> Data
          </Link>

          <div className="reveal flex items-baseline gap-3" style={{ animationDelay: "80ms" }}>
            <h1 className="display" style={{ fontSize: "var(--text-2xl)" }}>{decodedTable}</h1>
            {isReadOnly && (
              <span
                className="inline-flex items-center gap-1 border border-[var(--color-rule-strong)] px-2 py-0.5"
                style={{ fontSize: "9px", fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-ink-3)" }}
              >
                <Lock size={9} /> CALCULATED
              </span>
            )}
          </div>

          <p
            className="reveal mt-1.5"
            style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)", animationDelay: "140ms" }}
          >
            {loading
              ? "Loading…"
              : `${total.toLocaleString()} rows${isReadOnly ? " · Read-only — managed by the calculation engine" : ""}`}
          </p>
        </div>

        {!isReadOnly && !loading && (
          <button
            onClick={startAddRow}
            disabled={!!newRow}
            className="btn btn-ghost shrink-0 mb-0.5"
          >
            <Plus size={13} />
            Add row
          </button>
        )}
      </div>

      {/* ── Table area ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto min-h-0">
        {loading ? (
          <div className="px-8 pt-8" style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-3)" }}>
            Loading data…
          </div>
        ) : rows.length === 0 && !newRow ? (
          <div className="px-8 pt-8" style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-3)" }}>
            No rows on this page.
          </div>
        ) : (
          <table
            className="w-full border-collapse"
            style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)" }}
          >
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col}
                    className="px-3 py-2.5 text-left border-b border-r border-[var(--color-rule)] bg-[var(--color-paper)] whitespace-nowrap font-medium"
                    style={{ position: "sticky", top: 0, zIndex: 10, fontSize: "9px", letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--color-ink-3)" }}
                  >
                    {col}
                  </th>
                ))}
                {!isReadOnly && (
                  <th
                    className="px-2 py-2.5 bg-[var(--color-paper)] border-b border-[var(--color-rule)]"
                    style={{ position: "sticky", top: 0, zIndex: 10, width: 56 }}
                  />
                )}
              </tr>
            </thead>

            <tbody>
              {/* ── New row form ─────────────────────────────────────── */}
              {newRow && (
                <tr style={{ background: "var(--color-accent-soft)" }}>
                  {columns.map((col, i) => (
                    <td key={col} className="border-b border-r border-[var(--color-rule)] p-0">
                      <input
                        ref={i === 0 ? newRowFirstRef : null}
                        className="block w-full px-3 py-2 outline-none bg-transparent"
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: "var(--text-xs)",
                          minWidth: 80,
                          borderBottom: "2px solid var(--color-accent)",
                        }}
                        value={newRow[col]}
                        placeholder="—"
                        disabled={newRowSaving}
                        onChange={(e) => setNewRow((prev) => ({ ...prev, [col]: e.target.value }))}
                        onKeyDown={handleNewRowKeyDown}
                      />
                    </td>
                  ))}
                  <td className="border-b border-[var(--color-rule)] px-2" style={{ width: 56 }}>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={handleInsert}
                        disabled={newRowSaving}
                        className="p-1.5 rounded-sm transition-colors hover:bg-[var(--color-rule)]"
                        style={{ color: "var(--color-status-even)" }}
                        title="Save row (Ctrl+Enter)"
                      >
                        <Check size={13} />
                      </button>
                      <button
                        onClick={cancelNewRow}
                        disabled={newRowSaving}
                        className="p-1.5 rounded-sm transition-colors hover:bg-[var(--color-rule)]"
                        style={{ color: "var(--color-ink-3)" }}
                        title="Discard"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {/* ── Existing rows ────────────────────────────────────── */}
              {rows.map((row, i) => {
                const rowId   = row._rowid;
                const isSaving = savingRows.has(rowId);
                return (
                  <tr
                    key={rowId}
                    className={`group transition-opacity ${i % 2 !== 0 ? "bg-[var(--color-paper-2)]" : ""} ${isSaving ? "opacity-40" : ""}`}
                  >
                    {columns.map((col) => {
                      const isEditing = editCell?.rowId === rowId && editCell?.col === col;
                      const cellVal   = row[col];
                      return (
                        <td
                          key={col}
                          className={`border-b border-r border-[var(--color-rule)] p-0 ${!isReadOnly ? "cursor-text hover:bg-[var(--color-accent-soft)]" : ""} transition-colors`}
                          onClick={() => !isEditing && startEdit(rowId, col, cellVal)}
                        >
                          {isEditing ? (
                            <input
                              ref={inputRef}
                              className="block w-full px-3 py-2 outline-none bg-white border-2 border-[var(--color-accent)]"
                              style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", minWidth: 80 }}
                              value={editCell.value}
                              onChange={(e) => setEditCell((prev) => ({ ...prev, value: e.target.value }))}
                              onBlur={commitEdit}
                              onKeyDown={handleEditKeyDown}
                            />
                          ) : (
                            <span
                              className={`block px-3 py-2 truncate max-w-[200px] ${cellVal == null || cellVal === "" ? "italic" : ""}`}
                              style={cellVal == null || cellVal === "" ? { color: "var(--color-ink-3)" } : {}}
                            >
                              {cellVal == null ? "—" : String(cellVal)}
                            </span>
                          )}
                        </td>
                      );
                    })}

                    {!isReadOnly && (
                      <td className="border-b border-[var(--color-rule)] px-2" style={{ width: 56 }}>
                        <button
                          onClick={() => handleDelete(rowId)}
                          className="opacity-0 group-hover:opacity-100 p-1.5 rounded-sm transition-all hover:bg-[var(--color-rule)]"
                          style={{ color: "var(--color-ink-3)" }}
                          title="Delete row"
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Recalculate banner ───────────────────────────────────────────── */}
      {showBanner && (
        <div
          className="shrink-0 border-t border-[var(--color-rule)] px-8 py-3 flex items-center justify-between gap-4"
          style={{ background: "var(--color-paper-2)" }}
        >
          <div className="flex items-center gap-2.5">
            {recalcState === "running" && (
              <>
                <RefreshCw size={13} className="animate-spin" style={{ color: "var(--color-accent)" }} />
                <span style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-2)" }}>
                  Running calculators…
                </span>
              </>
            )}
            {recalcState === "done" && (
              <>
                <CheckCircle2 size={13} style={{ color: "var(--color-status-even)" }} />
                <span style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-2)" }}>
                  {recalcResult?.calculations_run?.length ?? 0} calculators updated
                  {Object.keys(recalcResult?.calculation_errors ?? {}).length > 0 &&
                    ` · ${Object.keys(recalcResult.calculation_errors).length} warnings`}
                </span>
              </>
            )}
            {(recalcState === "idle" || recalcState === "error") && isDirty && (
              <>
                <AlertTriangle size={13} style={{ color: "var(--color-ink-3)" }} />
                <span style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-2)" }}>
                  Data changed — calculated tables may be out of date
                </span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {(recalcState === "idle" || recalcState === "error") && (
              <button
                onClick={handleRecalculate}
                className="btn btn-ghost"
                style={{ fontSize: "var(--text-xs)", paddingBlock: "4px" }}
              >
                <RefreshCw size={11} />
                Recalculate
              </button>
            )}
            {recalcState !== "running" && (
              <button
                onClick={dismissBanner}
                className="p-1.5 rounded-sm hover:bg-[var(--color-rule)] transition-colors"
                style={{ color: "var(--color-ink-3)" }}
                title="Dismiss"
              >
                <X size={13} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      {!loading && totalPages > 1 && (
        <div
          className="shrink-0 border-t border-[var(--color-rule)] px-8 py-3 flex items-center justify-between"
          style={{ fontSize: "var(--text-xs)", color: "var(--color-ink-3)" }}
        >
          <span className="mono">
            {(page * PAGE_SIZE + 1).toLocaleString()}–{Math.min((page + 1) * PAGE_SIZE, total).toLocaleString()}
            {" of "}
            {total.toLocaleString()} rows
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 0}
              className="p-1.5 rounded-sm hover:bg-[var(--color-rule)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="mono px-3">{page + 1} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages - 1}
              className="p-1.5 rounded-sm hover:bg-[var(--color-rule)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
